import { useCallback, useEffect, useId, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { deleteDoc, doc, getDoc } from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useIsInsideAppLayout } from '../components/AppLayoutContext';
import DesignOutputWorkspace from '../components/designWorkspace/DesignOutputWorkspace';
import DesignWorkspaceShopSection from '../components/designWorkspace/DesignWorkspaceShopSection';
import { useAuth } from '../context/AuthContext';
import { db, storage } from '../firebase';
import { downloadUrlAsFile } from '../lib/downloadUrlAsFile';
import { storageObjectPathFromDownloadUrl } from '../lib/firebaseStoragePaths';
import { getProjectEditRoute, isInteriorShoppingEnabled, normalizeResultViewMode } from '../lib/projectToolType';
import { buildProjectTitle, projectCanCompare } from '../lib/projectWorkspaceUtils';
import Seo from '../seo/Seo';
import ShopFurniturePanel from '../components/ShopFurniturePanel';

function isStorageNotFound(err) {
  return err?.code === 'storage/object-not-found';
}

function projectViewModeStorageKey(projectId) {
  return `myroomsai:projectViewMode:${projectId}`;
}

function ProjectPage() {
  const isInsideAppLayout = useIsInsideAppLayout();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const shopOpenFromUrl = searchParams.get('shop') === '1';
  const [shopOpen, setShopOpen] = useState(shopOpenFromUrl);
  const [project, setProject] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloadError, setDownloadError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [projectPendingDelete, setProjectPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [shopItemCount, setShopItemCount] = useState(0);
  const deleteDialogTitleId = useId();
  const deleteDialogDescId = useId();
  const [resultViewMode, setResultViewMode] = useState('new');
  const [zoomedImage, setZoomedImage] = useState(
    /** @returns {{ src: string; alt: string } | null} */
    () => null,
  );

  useEffect(() => {
    if (!project) return;
    if (shopOpenFromUrl && isInteriorShoppingEnabled(project)) {
      setShopOpen(true);
      return;
    }
    if (shopOpenFromUrl && !isInteriorShoppingEnabled(project)) {
      setShopOpen(false);
      setSearchParams({}, { replace: true });
    }
  }, [shopOpenFromUrl, project, setSearchParams]);

  const openShop = useCallback(() => {
    if (!project || !isInteriorShoppingEnabled(project)) return;
    setShopOpen(true);
    setSearchParams({ shop: '1' }, { replace: true });
  }, [project, setSearchParams]);

  const closeShop = useCallback(() => {
    setShopOpen(false);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const handleShopItemsReady = useCallback(({ count }) => {
    setShopItemCount(count);
  }, []);

  const pageTitle = project ? buildProjectTitle(project) : 'Room design';
  const editRoute = project ? getProjectEditRoute(project) : null;
  const shoppingEnabled = project ? isInteriorShoppingEnabled(project) : false;

  const handleResultViewModeChange = useCallback(
    (mode) => {
      const canCompare = project ? projectCanCompare(project) : true;
      const hasOriginal = project ? Boolean(project.originalImageUrl) : true;
      const normalized = normalizeResultViewMode(mode, { canCompare, hasOriginal });
      setResultViewMode(normalized);
      if (projectId) {
        try {
          sessionStorage.setItem(projectViewModeStorageKey(projectId), normalized);
        } catch {
          /* ignore quota / private mode */
        }
      }
    },
    [project, projectId],
  );

  async function handleDownloadImage(url, baseName) {
    setDownloadError('');
    try {
      await downloadUrlAsFile(url, baseName, user);
    } catch (err) {
      setDownloadError(err?.message || 'Download failed.');
    }
  }

  const performDeleteProject = useCallback(
    async (p) => {
      if (!user?.uid || !p?.id) return;
      setDeleteError('');
      setDeleting(true);
      try {
        await deleteDoc(doc(db, 'projects', p.id));

        const origRef = ref(storage, `users/${user.uid}/projects/${p.id}/original`);
        try {
          await deleteObject(origRef);
        } catch (err) {
          if (!isStorageNotFound(err)) throw err;
        }

        if (p.generatedImageUrl) {
          const genPath = storageObjectPathFromDownloadUrl(p.generatedImageUrl);
          if (genPath) {
            try {
              await deleteObject(ref(storage, genPath));
            } catch (err) {
              if (!isStorageNotFound(err)) throw err;
            }
          }
        }
        setProjectPendingDelete(null);
        navigate('/projects', { replace: true });
      } catch (err) {
        setDeleteError(err?.message || 'Could not delete project.');
      } finally {
        setDeleting(false);
      }
    },
    [user?.uid, navigate],
  );

  useEffect(() => {
    if (!projectPendingDelete) return undefined;
    function onKeyDown(e) {
      if (e.key === 'Escape') setProjectPendingDelete(null);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [projectPendingDelete]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!projectId || !user?.uid) {
        setLoading(false);
        return;
      }
      setLoadError('');
      try {
        const snap = await getDoc(doc(db, 'projects', projectId));
        if (cancelled) return;
        if (!snap.exists()) {
          setProject(null);
          setLoadError('notfound');
        } else {
          const data = snap.data();
          if (data.userId !== user.uid) {
            setProject(null);
            setLoadError('forbidden');
          } else {
            setProject({ id: snap.id, ...data });
          }
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err?.message || 'load-failed');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [projectId, user?.uid]);

  useEffect(() => {
    setShopItemCount(0);
    if (!projectId) return;

    let storedMode = null;
    try {
      storedMode = sessionStorage.getItem(projectViewModeStorageKey(projectId));
    } catch {
      storedMode = null;
    }
    setResultViewMode(normalizeResultViewMode(storedMode));
  }, [projectId]);

  useEffect(() => {
    if (!project) return;
    setResultViewMode((prev) =>
      normalizeResultViewMode(prev, {
        canCompare: projectCanCompare(project),
        hasOriginal: Boolean(project.originalImageUrl),
      }),
    );
  }, [project]);

  useEffect(() => {
    if (!zoomedImage) return undefined;
    function handleEsc(event) {
      if (event.key === 'Escape') setZoomedImage(null);
    }
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [zoomedImage]);

  if (loadError === 'forbidden') {
    return <Navigate to="/projects" replace />;
  }

  return (
    <>
      <Seo
        title={`${pageTitle} | MyRoomsAI`}
        description="Review your uploaded room photo and generated redesign result, then download outputs from your project."
        canonicalPath={`/projects/${projectId || ''}`}
        noindex
      />
      {projectPendingDelete ? (
        <div
          className="delete-project-confirm-backdrop"
          role="presentation"
          onClick={() => !deleting && setProjectPendingDelete(null)}
        >
          <div
            className="card delete-project-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={deleteDialogTitleId}
            aria-describedby={deleteDialogDescId}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id={deleteDialogTitleId} className="delete-project-confirm-title">
              Delete this project?
            </h2>
            <p id={deleteDialogDescId} className="delete-project-confirm-desc">
              <strong>
                {[projectPendingDelete.roomType, projectPendingDelete.style]
                  .filter(Boolean)
                  .join(' · ') || 'This project'}
              </strong>{' '}
              will be removed permanently. This cannot be undone.
            </p>
            <div className="delete-project-confirm-actions">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={deleting}
                onClick={() => setProjectPendingDelete(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary delete-project-confirm-delete"
                disabled={deleting}
                onClick={() => performDeleteProject(projectPendingDelete)}
              >
                {deleting ? 'Deleting…' : 'Delete project'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {!isInsideAppLayout ? <Navbar /> : null}
      <main className="dashboard-page new-design-page project-detail-page">
        <div className="container">
          {shopOpen && shoppingEnabled ? (
            !loading && !loadError && project ? (
              <header className="project-result-toolbar dashboard-page-toolbar dashboard-header project-detail-shop-toolbar">
                <div className="project-result-toolbar-start">
                  <button
                    type="button"
                    className="project-result-btn project-result-btn--secondary project-detail-back-to-project"
                    onClick={closeShop}
                  >
                    <ArrowLeft size={18} strokeWidth={2} aria-hidden />
                    Back
                  </button>
                </div>
              </header>
            ) : null
          ) : null}

          {deleteError ? (
            <p className="dashboard-inline-error" role="alert">
              {deleteError}
            </p>
          ) : null}

          {loading ? (
            <p className="project-page-muted">Loading…</p>
          ) : loadError === 'notfound' || !project ? (
            <div className="project-result-empty-card">
              <p>We could not find this project.</p>
              <Link
                to="/projects"
                className="project-result-btn project-result-btn--secondary"
              >
                <ArrowLeft size={18} strokeWidth={2} aria-hidden />
                Back to Projects
              </Link>
            </div>
          ) : loadError ? (
            <p className="dashboard-inline-error" role="alert">
              {loadError}
            </p>
          ) : (
            <>
              {shopOpen && shoppingEnabled && project.generatedImageUrl ? (
                <div className="project-result-content">
                  <div className="shop-furniture-panel-wrap project-result-shop-panel">
                    <ShopFurniturePanel
                      projectId={projectId}
                      generatedImageUrl={project.generatedImageUrl}
                      onClose={closeShop}
                    />
                  </div>
                  {downloadError ? (
                    <p className="dashboard-inline-error project-detail-download-error" role="alert">
                      {downloadError}
                    </p>
                  ) : null}
                </div>
              ) : (
                <DesignOutputWorkspace
                  project={project}
                  resultViewMode={resultViewMode}
                  onResultViewModeChange={handleResultViewModeChange}
                  onEdit={editRoute ? () => navigate(editRoute) : undefined}
                  onDelete={() => setProjectPendingDelete(project)}
                  onOpenShop={shoppingEnabled ? openShop : undefined}
                  shopItemCount={shopItemCount}
                  showEdit={Boolean(editRoute)}
                  onDownloadImage={handleDownloadImage}
                  onZoomImage={setZoomedImage}
                  downloadError={downloadError}
                />
              )}

              <DesignWorkspaceShopSection
                project={project}
                projectId={projectId}
                generatedImageUrl={String(project.generatedImageUrl || '')}
                shopOpen={shopOpen}
                onOpenShop={openShop}
                onItemsReady={handleShopItemsReady}
              />
            </>
          )}
        </div>
        {!isInsideAppLayout ? <Footer /> : null}
      </main>
      {zoomedImage ? (
        <div
          className="image-lightbox-backdrop"
          role="presentation"
          onClick={() => setZoomedImage(null)}
        >
          <div
            className="image-lightbox-content"
            role="dialog"
            aria-modal="true"
            aria-label="Image preview"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="image-lightbox-close"
              aria-label="Close image preview"
              onClick={() => setZoomedImage(null)}
            >
              Close
            </button>
            <img
              src={zoomedImage.src}
              alt={zoomedImage.alt}
              className="image-lightbox-img"
              loading="eager"
              decoding="sync"
            />
          </div>
        </div>
      ) : null}
    </>
  );
}

export default ProjectPage;
